import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

function lineText(t, block) {
  if (!block) return t("history.notProvided");
  const items = Array.isArray(block.items)
    ? block.items.map((item) => String(item).trim()).filter(Boolean)
    : [];
  const other = typeof block.other === "string" ? block.other.trim() : "";
  const parts = other && !items.includes(other) ? [...items, other] : items;
  if (parts.length > 0) return parts.join(", ");
  if (block.none_reported) return t("history.noneReported");
  return t("history.notProvided");
}

function ProfileLine({ label, value, updatedAt, href, t }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-border last:border-0">
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm break-words">{value}</p>
        {updatedAt ? (
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {t("history.updated").replace("{date}", String(updatedAt).slice(0, 10))}
          </p>
        ) : null}
      </div>
      {href ? (
        <Link to={href} className="text-xs text-primary shrink-0 hover:underline">
          {t("history.review")}
        </Link>
      ) : null}
    </div>
  );
}

export default function SummaryCard({ summary, subjectId, readOnly = false, omitClinical = false }) {
  const { t } = useLanguage();
  if (!summary) return null;
  const profileHref = !readOnly && subjectId ? `/people/${subjectId}` : null;
  const tier = summary.tier_a || {};
  const tierValue = [
    tier.age_display ? String(tier.age_display) : null,
    tier.sex_at_birth ? t(`healthProfile.sex.${tier.sex_at_birth}`) : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const latest = summary.latest_by_category || {};
  const storage = summary.storage || {};

  return (
    <Card data-testid="history-summary">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t("history.summary")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <ProfileLine
          t={t}
          label={t("history.tierA")}
          value={tierValue || t("history.notProvided")}
          updatedAt={tier.updated_at}
          href={profileHref}
        />
        {omitClinical ? null : (
          <>
            <ProfileLine
              t={t}
              label={t("history.conditions")}
              value={lineText(t, summary.conditions)}
              updatedAt={summary.conditions?.updated_at}
              href={profileHref}
            />
            <ProfileLine
              t={t}
              label={t("history.medications")}
              value={lineText(t, summary.medications)}
              updatedAt={summary.medications?.updated_at}
              href={profileHref}
            />
            <ProfileLine
              t={t}
              label={t("history.allergies")}
              value={lineText(t, summary.allergies)}
              updatedAt={summary.allergies?.updated_at}
              href={profileHref}
            />
          </>
        )}
        <p className="text-xs text-muted-foreground pt-2" data-testid="history-record-count">
          {t("history.recordCount").replace("{count}", String(summary.record_count || 0))}
          {readOnly ? "" : (
            <>
              {" · "}
              {t("history.storageUsage")
                .replace("{used}", String(storage.used_mb ?? 0))
                .replace("{limit}", String(storage.limit_mb ?? 0))}
            </>
          )}
        </p>
        {Object.keys(latest).length > 0 && (
          <div className="pt-2">
            <p className="text-xs text-muted-foreground mb-1">{t("history.latest")}</p>
            <ul className="space-y-1">
              {Object.entries(latest).map(([category, item]) => (
                <li key={category} className="text-sm">
                  <span className="text-muted-foreground">
                    {t(`history.category.${category}`)}
                    {": "}
                  </span>
                  {item.title || t("history.dateUnknown")}
                  {item.event_date ? ` · ${item.event_date}` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
